import { getStoredToken } from '../auth/cognito';

const API_URL = 'https://0emko5cw6f.execute-api.ca-central-1.amazonaws.com/prod';

function getAuthHeaders() {
  const token = getStoredToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': token || ''
  };
}

export interface AssessmentResult {
  userId: string;
  assessmentId: string;
  assessmentType: 'tabletop' | 'engineering';
  moduleId: string;
  score: number;
  answers: number[];
  isComplete: boolean;
  completedAt: string;
}

export interface AdminUser {
  userId: string;
  assessments: AssessmentResult[];
  lastActive: string;
}

export async function getAllResults(): Promise<AssessmentResult[]> {
  try {
    const res = await fetch(`${API_URL}/admin/results`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const data = await res.json();
    return data.results || [];
  } catch (err) {
    console.error('Failed to fetch results:', err);
    return [];
  }
}

export async function getAllUsers(): Promise<AdminUser[]> {
  try {
    const res = await fetch(`${API_URL}/admin/users`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    const data = await res.json();
    return data.users || [];
  } catch (err) {
    console.error('Failed to fetch users:', err);
    return [];
  }
}

export function getCompletedResults(results: AssessmentResult[]) {
  return results.filter(r => r.isComplete);
}

export function getAvgScore(results: AssessmentResult[]) {
  const completed = results.filter(r => r.isComplete);
  if (completed.length === 0) return 0;
  return Math.round(completed.reduce((a, b) => a + b.score, 0) / completed.length);
}

export function getRisk(score: number) {
  if (score >= 80) return { label: 'Low', color: '#22c55e' };
  if (score >= 50) return { label: 'Medium', color: '#f97316' };
  return { label: 'High', color: '#ef4444' };
}

export interface Org {
  orgId: string;
  name: string;
  domain: string;
  plan: string;
  seats: number;
  seatsUsed: number;
  status: string;
  billingEmail: string;
  trialEndsAt?: string;
  createdAt: string;
}

export interface OrgUser {
  userId: string;
  orgId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  invitedAt: string;
  lastActive: string | null;
}

export async function getOrgs(): Promise<Org[]> {
  try {
    const res = await fetch(`${API_URL}/super/orgs`, { headers: getAuthHeaders() });
    const data = await res.json();
    return data.orgs || [];
  } catch { return []; }
}

export async function createOrg(payload: { name: string; domain: string; plan: string; seats: number; billingEmail: string }): Promise<{ success?: boolean; orgId?: string; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/orgs`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function updateOrg(orgId: string, updates: Partial<{ plan: string; seats: number; status: string; billingEmail: string }>): Promise<{ success?: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/orgs/${orgId}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(updates) });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function getOrgUsers(orgId: string): Promise<OrgUser[]> {
  try {
    const res = await fetch(`${API_URL}/super/orgs/${orgId}/users`, { headers: getAuthHeaders() });
    const data = await res.json();
    return data.users || [];
  } catch { return []; }
}

export async function deleteOrg(orgId: string): Promise<{ success?: boolean; deletedUsers?: number; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/orgs/${orgId}`, { method: 'DELETE', headers: getAuthHeaders() });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function deletePublicOrg(orgId: string): Promise<{ success?: boolean; deletedUsers?: number; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/public/orgs/${orgId}`, { method: 'DELETE', headers: getAuthHeaders() });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function removeOrgUser(userId: string): Promise<{ success?: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/org/users/${userId}`, { method: 'DELETE', headers: getAuthHeaders() });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function deleteEnterpriseUser(userId: string): Promise<{ success?: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/users/${userId}`, { method: 'DELETE', headers: getAuthHeaders() });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function deletePublicUser(userId: string): Promise<{ success?: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/public/users/${userId}`, { method: 'DELETE', headers: getAuthHeaders() });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export interface PublicOrg {
  orgId: string;
  name: string;
  domain: string;
  joinCode: string;
  plan: string;
  seats: number;
  seatsUsed: number;
  status: string;
  billingEmail: string;
  trialEndsAt?: string;
  createdAt: string;
}

export async function getPublicOrgs(): Promise<PublicOrg[]> {
  try {
    const res = await fetch(`${API_URL}/super/public/orgs`, { headers: getAuthHeaders() });
    const data = await res.json();
    return data.orgs || [];
  } catch { return []; }
}

export async function createPublicOrg(payload: { name: string; domain: string; plan: string; seats: number; billingEmail: string }): Promise<{ success?: boolean; orgId?: string; joinCode?: string; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/public/orgs`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function updatePublicOrg(orgId: string, updates: Partial<{ plan: string; seats: number; status: string; billingEmail: string }>): Promise<{ success?: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/public/orgs/${orgId}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(updates) });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function assignUserToPublicOrg(orgId: string, userId: string, email: string, role: string = 'user'): Promise<{ success?: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/public/orgs/${orgId}/assign`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ userId, email, role }) });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function invitePublicOrgUser(orgId: string, email: string, firstName: string, lastName: string, role: string = 'user'): Promise<{ success?: boolean; userId?: string; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/public/orgs/${orgId}/invite`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ email, firstName, lastName, role }) });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function getPublicOrgUsers(orgId: string): Promise<OrgUser[]> {
  try {
    const res = await fetch(`${API_URL}/super/public/orgs/${orgId}/users`, { headers: getAuthHeaders() });
    const data = await res.json();
    return data.users || [];
  } catch { return []; }
}

export async function resendPublicOrgInvite(orgId: string, userId: string): Promise<{ success?: boolean; message?: string; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/public/orgs/${orgId}/users/${userId}/resend`, { method: 'POST', headers: getAuthHeaders() });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export interface PublicUser {
  userId: string;
  email: string;
  status: string;
  createdAt: string;
  lastModified: string;
  enabled: boolean;
  orgId: string | null;
  orgRole: string | null;
}

export async function overridePublicUserPlan(userId: string, plan: 'paid' | 'free'): Promise<{ success?: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/public/users/${userId}/plan`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ plan }),
    });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function getPublicUsers(): Promise<PublicUser[]> {
  try {
    const res = await fetch(`${API_URL}/super/public/users`, { headers: getAuthHeaders() });
    const data = await res.json();
    return data.users || [];
  } catch { return []; }
}

export interface SuperAdmin {
  userId: string;
  email: string;
  status: string;
  createdAt: string;
  enabled: boolean;
}

export async function getPublicSuperAdmins(): Promise<SuperAdmin[]> {
  try {
    const res = await fetch(`${API_URL}/super/public/admins`, { headers: getAuthHeaders() });
    const data = await res.json();
    return data.admins || [];
  } catch { return []; }
}

export async function addPublicSuperAdmin(email: string): Promise<{ success?: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/public/admins`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ email }) });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function removePublicSuperAdmin(userId: string): Promise<{ success?: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/public/admins/${userId}`, { method: 'DELETE', headers: getAuthHeaders() });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function getSuperAdmins(): Promise<SuperAdmin[]> {
  try {
    const res = await fetch(`${API_URL}/super/admins`, { headers: getAuthHeaders() });
    const data = await res.json();
    return data.admins || [];
  } catch { return []; }
}

export async function addSuperAdmin(email: string): Promise<{ success?: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/admins`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ email }) });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function removeSuperAdmin(userId: string): Promise<{ success?: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_URL}/super/admins/${userId}`, { method: 'DELETE', headers: getAuthHeaders() });
    return await res.json();
  } catch { return { error: 'Network error.' }; }
}

export async function inviteUser(email: string, firstName: string, lastName: string, orgId: string, role: string = 'user'): Promise<{ success?: boolean; error?: string; message?: string }> {
  try {
    const res = await fetch(`${API_URL}/admin/invite`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ email, firstName, lastName, orgId, role })
    });
    return await res.json();
  } catch (err) {
    return { error: 'Network error. Please try again.' };
  }
}